import { useCallback, useEffect, useRef, useState } from "react";

import { useLocation, useParams } from "react-router-dom";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import Loading from "@/components/Loading";
import OrderListItem from "@/components/organisations/orders/ui/OrderListItem";
import PatientPayExpiredLinkPage from "@/components/patientPay/PatientPayExpiredLinkPage";
import PatientPayOrderDetailsForm from "@/components/patientPay/PatientPayOrderDetailsForm";
import PatientPayStatusPage from "@/components/patientPay/PatientPayStatusPage";
import { BASKET_PAYEE_PATIENT, PAYMENT_INITIATED } from "@/core/constants";
import { theme } from "@/core/theme";
import {
  PATIENT_PAY_CHECKOUT_PAGE_QUERY,
  PROCEED_TO_PATIENT_PAY_CHECKOUT_MUTATION
} from "@/graphql/patientPay";
import Currency from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import B2CHeader from "@/tpo/header/B2CHeader";
import ButtonV2 from "@/v2/Buttons";
import { useMutation, useQuery } from "@apollo/client";

function PatientPayOrderTotals({ basket, totalDiscount }) {
  if (!basket) {
    return null;
  }

  return (
    <Stack gap={10} alignItems="flex-end">
      {totalDiscount > 0 && (
        <Currency
          symbol={basket.currencySymbol}
          value={totalDiscount * -1}
          prefix="DISCOUNT"
          fontSize={28}
        />
      )}
      <Currency
        prefix="SHIPPING"
        value={basket.basketShippingCost}
        fontSize={28}
        symbol={basket.currencySymbol}
      />
      <Currency
        prefix="TOTAL"
        value={basket.basketTotalPrice}
        fontSize={44}
        symbol={basket.currencySymbol}
      />
    </Stack>
  );
}

function PatientPayWhiteBox({ children }) {
  return (
    <Box
      bg="white"
      borderRadius={20}
      maxWidth={720}
      mx="auto"
      width="100%"
      pt={[30, 30, 60]}
      pb={[50, 50, 80]}
      px={[20, 20, 40]}
    >
      <Stack gap={40} maxWidth={720} mx="auto">
        {children}
      </Stack>
    </Box>
  );
}

function PatientPayOrderPageLayout({ children }) {
  return (
    <>
      <B2CHeader />
      <CustomerPage
        bg="haze"
        jumbotronProps={{
          title: "Order Detail",
          bg: "purple",
          color: "white"
        }}
      >
        <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={20}>
          <Stack gap={40} maxWidth={720} mx="auto" width="100%">
            {children}
          </Stack>
        </Box>
      </CustomerPage>
    </>
  );
}

function PatientPayErrorState({ message }) {
  return <PatientPayStatusPage title="Unable to load order">{message}</PatientPayStatusPage>;
}

function isValidPatientPayCheckout(data) {
  const basket = data?.order?.sourceBasket;

  return (
    basket?.payee === BASKET_PAYEE_PATIENT &&
    data?.order?.status === PAYMENT_INITIATED &&
    data?.patientProfile
  );
}

function getQueryErrorMessage(error) {
  return error?.graphQLErrors?.[0]?.message || error?.message || "Request failed.";
}

function isPatientPayAuthError(message) {
  return (
    message.includes("Invalid authentication token") ||
    message.includes("You do not have permission to perform this action")
  );
}

function getAuthTokenFromUrl() {
  return new URLSearchParams(window.location.search).get("auth");
}

function stripAuthTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("auth")) {
    return;
  }

  params.delete("auth");
  const search = params.toString();
  const url = search ? `${window.location.pathname}?${search}` : window.location.pathname;
  window.history.replaceState({}, "", url);
}

export default function PatientPayOrderPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [authToken] = useState(getAuthTokenFromUrl);
  const linkExpired = searchParams.get("expired") === "1";

  useEffect(() => {
    stripAuthTokenFromUrl();
  }, []);

  const [proceedError, setProceedError] = useState(null);
  const orderDetailsFormRef = useRef(null);

  const { data, loading, error } = useQuery(PATIENT_PAY_CHECKOUT_PAGE_QUERY, {
    variables: { orderId, authToken: authToken || null },
    skip: linkExpired || !orderId,
    fetchPolicy: "network-only"
  });

  const [proceedToPayment, { loading: proceedingToPayment }] = useMutation(
    PROCEED_TO_PATIENT_PAY_CHECKOUT_MUTATION
  );

  const handleContinueToPayment = useCallback(async () => {
    setProceedError(null);

    const proceedInput = orderDetailsFormRef.current?.getProceedInput();
    if (!proceedInput) {
      setProceedError("Something went wrong. Please try again later.");
      return;
    }

    try {
      const response = await proceedToPayment({
        variables: {
          input: proceedInput
        }
      });

      const mutationResult = response.data?.proceedToPatientPayCheckoutMutation;
      if (!mutationResult) {
        setProceedError("Something went wrong. Please try again later.");
        return;
      }

      if (mutationResult.errors?.length) {
        orderDetailsFormRef.current?.applyServerErrors(mutationResult.errors);
        return;
      }

      if (mutationResult.checkoutUrl) {
        window.location.assign(mutationResult.checkoutUrl);
      }
    } catch (err) {
      setProceedError(err.message || "Something went wrong. Please try again later.");
    }
  }, [authToken, orderId, proceedToPayment]);

  if (linkExpired) {
    return <PatientPayExpiredLinkPage orderId={orderId} />;
  }

  if (loading) {
    return (
      <PatientPayOrderPageLayout>
        <PatientPayWhiteBox>
          <Loading />
        </PatientPayWhiteBox>
      </PatientPayOrderPageLayout>
    );
  }

  if (error) {
    const errorMessage = getQueryErrorMessage(error);
    if (isPatientPayAuthError(errorMessage)) {
      return <PatientPayExpiredLinkPage orderId={orderId} />;
    }
    return <PatientPayErrorState message={errorMessage} />;
  }

  const pageData = data?.patientPayCheckoutPage;

  if (!pageData) {
    return <PatientPayErrorState message="This order could not be found." />;
  }

  if (pageData.alreadyPaid) {
    return (
      <PatientPayStatusPage title="Order already paid">
        This order has already been paid. Thank you.
      </PatientPayStatusPage>
    );
  }

  if (!isValidPatientPayCheckout(pageData)) {
    return <PatientPayErrorState message="This order could not be found." />;
  }

  if (pageData.paymentAvailable === false) {
    return (
      <PatientPayStatusPage title="Unable to complete checkout">
        We&apos;re sorry, but we are unable to take payment for this order at this time. Please
        contact your healthcare practitioner or info@regeneruslabs.com for assistance.
        {orderId ? (
          <>
            <br />
            <br />
            Order ID: {orderId}
          </>
        ) : null}
      </PatientPayStatusPage>
    );
  }

  const order = pageData.order;
  const basket = order.sourceBasket;

  return (
    <PatientPayOrderPageLayout>
      <PatientPayWhiteBox>
        <Stack gap={16}>
          <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]}>
            Your order details
          </Box>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
            Check the below details before proceeding to payment
          </Box>
        </Stack>

        <Stack gap={18}>
          <Group gap={10}>
            <Box fontFamily="gilroyBold" fontSize={18}>
              Order number:
            </Box>
            <Box fontFamily="gilroyMedium" fontSize={18}>
              {order.id}
            </Box>
          </Group>
          {order.checkoutDate && (
            <Group gap={10}>
              <Box fontFamily="gilroyBold" fontSize={18}>
                Order date:
              </Box>
              <Box fontFamily="gilroyMedium" fontSize={18}>
                {new Date(order.checkoutDate).toLocaleDateString("en-GB")}
              </Box>
            </Group>
          )}
        </Stack>

        <Stack gap={20}>
          {order.testItems?.map(lineItem => (
            <OrderListItem
              key={lineItem.id}
              consumerPerspective={true}
              order={order}
              orderTestItem={lineItem}
              name={lineItem.nameInBasket}
              price={lineItem.price}
              clinicLocation={lineItem.clinicLocation}
              clinicBooking={lineItem.clinicBooking}
              isAddOn={!!lineItem.parent}
              currencySymbol={basket?.currencySymbol}
              registeredUser={lineItem.registeredUser}
              showBadges={false}
            />
          ))}
          {order.supplementItems?.map(lineItem => (
            <OrderListItem
              key={lineItem.id}
              consumerPerspective={true}
              order={order}
              orderTestItem={lineItem}
              name={lineItem.nameInBasket}
              price={lineItem.price}
              currencySymbol={basket?.currencySymbol}
              showBadges={false}
            />
          ))}
        </Stack>
        <PatientPayOrderTotals basket={basket} totalDiscount={order.totalDiscount} />
      </PatientPayWhiteBox>

      <PatientPayWhiteBox>
        <PatientPayOrderDetailsForm
          ref={orderDetailsFormRef}
          authToken={authToken}
          order={order}
          patientProfile={pageData.patientProfile}
          basket={basket}
          readOnly={!pageData.canProceedToPayment}
        />
      </PatientPayWhiteBox>

      <Stack gap={16} alignItems="center" width="100%">
        {proceedError && (
          <Box
            color="red"
            fontFamily="gilroyMedium"
            fontSize={[14, 14, 16]}
            maxWidth={720}
            textAlign="center"
          >
            {proceedError}
          </Box>
        )}
        {pageData.canProceedToPayment ? (
          <ButtonV2
            color="green"
            disabled={proceedingToPayment}
            onClick={handleContinueToPayment}
            size={["sm", "sm", "md"]}
          >
            Continue to payment
          </ButtonV2>
        ) : (
          <Box
            fontFamily="gilroyMedium"
            fontSize={[14, 14, 16]}
            maxWidth={720}
            textAlign="center"
          >
            Only the patient can complete payment for this order.
          </Box>
        )}
      </Stack>
    </PatientPayOrderPageLayout>
  );
}
